import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { 
  Target, 
  Eye, 
  Users, 
  Building2,
  ArrowRight,
  Lightbulb,
  Heart,
  Sparkles,
  Shield,
  Zap
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="pt-20">
      <SEO
        title="Über uns – KI-Agentur aus Magdeburg & Braunschweig"
        description="LAIzyones: Ihre KI-Agentur aus Magdeburg und Braunschweig. Wir machen künstliche Intelligenz für jedes Unternehmen zugänglich. Lernen Sie unser Team kennen."
        path="/ueber-uns"
        keywords="LAIzyones Team, KI Agentur Magdeburg, KI Beratung Braunschweig, David Ehrle, KI Unternehmen Deutschland, über LAIzyones"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Über uns' },
        ]}
      />
      <HeroSection />
      <MissionVision />
      <ValuesSection />
      <TeamSection />
      <CTASection />
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="section bg-gray-50 relative overflow-hidden">
      {/* Grid Pattern */}
      <div className="absolute inset-0 grid-pattern" />
      
      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="badge mb-6 fade-up">Über LAIzyones</div>
          <div className="reveal-container">
            <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
              Ihre <span className="gradient-text">KI-Agentur</span> aus Sachsen-Anhalt
            </h1>
          </div>
          <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up">
            LAIzyones ist ein Technologieunternehmen mit Sitz in Sachsen-Anhalt, spezialisiert auf
            KI-Beratung und Prozessautomatisierung für den deutschen Mittelstand.
            Wir machen künstliche Intelligenz für Unternehmen jeder Größe zugänglich — von Magdeburg über Braunschweig bis in ganz Deutschland.
          </p>
        </div>
      </div>
    </section>
  );
};

// Mission & Vision
const MissionVision = () => {
  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Mission */}
          <div className="card group animate-card">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-6 group-hover:from-emerald-200 group-hover:to-cyan-200 transition-colors">
              <Target className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-['Space_Grotesk'] font-bold text-2xl text-gray-900 mb-4">
              Unsere Mission: KI-Demokratisierung
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Wir machen fortschrittliche KI-Technologie für Unternehmen jeder Größe 
              zugänglich. Unsere Mission ist es, durch intelligente Prozessautomatisierung 
              und datengestützte Einblicke den Geschäftserfolg unserer Kunden zu 
              beschleunigen — besonders im deutschen Mittelstand, wo enormes ungenutztes 
              KI-Potenzial liegt.
            </p>
          </div>

          {/* Vision */}
          <div className="card group animate-card">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-6 group-hover:from-emerald-200 group-hover:to-cyan-200 transition-colors">
              <Eye className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-['Space_Grotesk'] font-bold text-2xl text-gray-900 mb-4">
              Unsere Vision: KI für jedes Unternehmen
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Wir streben eine Zukunft an, in der jedes Unternehmen in Deutschland von den Vorteilen 
              künstlicher Intelligenz profitieren kann. Unser Ziel: der führende Partner 
              für KI-gestützte Geschäftstransformation und Prozessautomatisierung im 
              deutschsprachigen Raum werden.
            </p>
          </div>
        </div>

        {/* Company Background */}
        <div className="mt-12 tech-card p-8 md:p-12 fade-up">
          <div className="flex flex-col lg:flex-row items-start gap-8">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <div>
              <h3 className="font-['Space_Grotesk'] font-bold text-2xl text-white mb-4">
                Unser Hintergrund
              </h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                LAIzyones wurde mit der Vision gegründet, die Leistungsfähigkeit moderner 
                KI-Technologie für den Mittelstand nutzbar zu machen. Unser Team vereint 
                Expertise in <span className="text-emerald-400">künstlicher Intelligenz</span>, 
                Softwareentwicklung und Geschäftsprozessoptimierung.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Mit Produkten wie <span className="text-emerald-400">LAIzyleads</span> für automatisierte Lead-Generierung helfen wir Unternehmen 
                dabei, ihre Vertriebsprozesse zu optimieren, bessere Geschäftsentscheidungen 
                zu treffen und nachhaltig zu wachsen — aus Sachsen-Anhalt für ganz Deutschland.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Values Section
const ValuesSection = () => {
  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Wir entwickeln kontinuierlich neue Lösungen und bleiben an der Spitze der KI-Technologie.',
    },
    {
      icon: Heart,
      title: 'Kundenorientierung',
      description: 'Der Erfolg unserer Kunden steht im Mittelpunkt all unserer Aktivitäten.',
    },
    {
      icon: Sparkles,
      title: 'Exzellenz',
      description: 'Wir streben nach höchster Qualität in allem, was wir tun.',
    },
    {
      icon: Shield,
      title: 'Vertrauen',
      description: 'Datenschutz und Sicherheit sind für uns keine Kompromisse.',
    },
    {
      icon: Zap,
      title: 'Agilität',
      description: 'Schnelle Anpassung an neue Anforderungen und Marktveränderungen.',
    },
    {
      icon: Users,
      title: 'Partnerschaft',
      description: 'Langfristige Beziehungen statt kurzfristiger Transaktionen.',
    },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unsere Werte</div>
          <div className="reveal-container">
            <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 reveal-text mb-4">
              Wofür wir <span className="gradient-text">stehen</span>
            </h2>
          </div>
          <p className="body-text text-lg text-gray-600 fade-up">
            Unsere Werte leiten uns bei allem, was wir tun – von der Produktentwicklung 
            bis zur Kundenbetreuung.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div 
                key={index}
                className="card text-center animate-card"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-xl text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const teamMembers = [
    {
      name: 'David Ehrle',
      role: 'Founder',
      image: 'https://customer-assets.emergentagent.com/job_9cad2fca-3a0b-48e9-b6a8-1324de5e746f/artifacts/oi9df12x_Screendavid.png',
      imgStyle: { objectPosition: 'center 20%' },
    },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unser Team</div>
          <div className="reveal-container">
            <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 reveal-text mb-4">
              Die Köpfe hinter <span className="gradient-text">LAIzyones</span>
            </h2>
          </div>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Erfahrene Gründer mit einer gemeinsamen Vision: KI-Automatisierung für den deutschen Mittelstand zugänglich machen — praxisnah und ergebnisorientiert.
          </p>
        </div>

        <div className="flex justify-center gap-16 flex-wrap">
          {teamMembers.map((member, index) => (
            <div 
              key={index}
              data-testid={`team-member-${index}`}
              className="text-center animate-card"
            >
              <div className="w-40 h-40 rounded-full mx-auto mb-5 overflow-hidden border-4 border-white shadow-xl ring-2 ring-emerald-100">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-full h-full object-cover"
                  style={member.imgStyle}
                />
              </div>
              <h3 className="font-['Space_Grotesk'] font-semibold text-xl text-gray-900">
                {member.name}
              </h3>
              <p className="text-emerald-600 text-sm font-medium mt-1">
                {member.role}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 to-cyan-600 p-8 md:p-12 lg:p-16">
          {/* Background Pattern */}
          <div className="absolute inset-0 grid-pattern opacity-10" />
          
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
              Starten Sie Ihre KI-Transformation mit LAIzyones
            </h2>
            <p className="text-emerald-100 text-base md:text-lg mb-8">
              Kostenlose Erstberatung: Lassen Sie uns gemeinsam herausfinden, wie KI-Automatisierung 
              Ihre Geschäftsprozesse optimieren und Kosten nachhaltig senken kann.
            </p>
            <Link
              to="/#kontakt"
              className="inline-flex items-center justify-center gap-2 bg-white text-emerald-600 font-semibold px-8 py-4 rounded-xl hover:bg-emerald-50 transition-colors"
            >
              Kontakt aufnehmen
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPage;
